import React, { ReactNode, useState } from 'react';
import { Button, Heading4, Heading5, Link } from 'is24-corecss';
import { css } from '@emotion/css';
import { IconS24ChevronRight24 } from '@is24/cosma-ui-icons';
import schufaWithButton from '../images/schufa_with_button.png';
import schufaWithButtonMobile from '../images/schufa_with_button_mobile.png';
import { SchufaPreview } from './SchufaPreview';
import { Msg, useIntl } from '@/i18n';
import { APP_CONFIG } from '@/config';
import de from '@/i18n/locales/de';
import { SolvencyInfoResponse } from '@/@types/solvency';

const is24SolvencyUrl = `${APP_CONFIG.bonitaetsCheckUrl}?cmp_id=10-052075&cmp_name=residential_profile&cmp_position=profile_solvency&cmp_creative=create_solvency_tile`;
const schufaCheckUrl = `${APP_CONFIG.schufaCheckUrl}?cmp_id=10-051402&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=solvency_cta`;

const styles = {
  container: css`
    padding: 16px;
    @media (min-width: 780px) {
      max-width: 650px;
      margin: 0 auto;
    }
  `,
  borderRound: css`
    border-radius: 4px;
  `,
  previewImage: css`
    cursor: pointer;
    max-height: 104px;
    @media (min-width: 669px) {
      max-height: 170px;
      max-width: 215px;
    }
  `,
  solvencyBenefitContent: css`
    @media (min-width: 669px) {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }
  `,
  createSolvencyCta: css`
    margin-bottom: 4px;
    margin-top: 32px;
  `,
  discountedPrice: css`
    text-decoration: line-through;
    display: inline-block;
    opacity: 0.5;
  `,
  bold: css`
    font-weight: bold;
  `,
};

interface SchufaChoiceProps {
  solvencyInfo?: SolvencyInfoResponse;
}

const AlreadyBoughtBanner: React.FC = () => {
  const { t } = useIntl();

  return (
    <div className={'padding-bottom-m palm-padding-bottom-l grid-flex'}>
      <span>
        {t({
          id: 'benefits.schufaCheckMieterPlus.schufaModal.alreadyBought.text',
          defaultMessage: 'Bereits bestellt? ',
        })}
      </span>
      <Link href={APP_CONFIG.rentProfileUrl} variant='default' rightIcon={<IconS24ChevronRight24 />}>
        {t({
          id: 'benefits.schufaCheckMieterPlus.schufaModal.alreadyBought.link',
          defaultMessage: 'Jetzt einsehen',
        })}
      </Link>
    </div>
  );
};

const SchufaSolvencyCard: React.FC<{
  solvencyInfo?: SolvencyInfoResponse;
  setShowPreview: (showPreview: boolean) => void;
}> = ({ solvencyInfo, setShowPreview }) => {
  const { t } = useIntl();

  return (
    <div
      className={`border padding-l ${styles.borderRound} height-full`}
      id={'schufa-solvency-frame'}
      data-testid='schufa-solvency-card'
    >
      <div>
        <Heading5 className={'margin-none'}>
          {t({
            id: 'benefits.schufaCheckMieterPlus.schufaModal.modalTitle',
            defaultMessage: 'SCHUFA-BonitätsCheck',
          })}
        </Heading5>
        <div className={'padding-bottom-l padding-top-xs'}>
          <Msg
            id='benefits.schufaCheckMieterPlus.schufaModal.schufaIncluded'
            defaultMessage='Für MieterPlus-Mitglieder '
            values={{
              b: (children: ReactNode) => <b>{children}</b>,
            }}
          />
          {solvencyInfo?.discounted && (
            <span className={styles.discountedPrice}>
              <Msg id='benefits.schufaCheckMieterPlus.schufaModal.schufaOriginalPrice' defaultMessage='29,95 €' />
            </span>
          )}
          {solvencyInfo?.price && (
            <span className={styles.bold}>
              <Msg
                id='benefits.schufaCheckMieterPlus.schufaModal.schufaDiscounted'
                defaultMessage=' nur {discountedPrice} €'
                values={{ discountedPrice: solvencyInfo.price }}
              />
            </span>
          )}
        </div>
      </div>
      <div className={styles.solvencyBenefitContent}>
        <Button
          className={'padding-none palm-margin-bottom-l'}
          onClick={() => setShowPreview(true)}
          textStyle
          data-testid='show-preview-button'
        >
          <img
            id={'schufa-solvency-image'}
            className={`${styles.previewImage} palm-hide padding-none`}
            src={schufaWithButton}
            alt={''}
          />
          <img
            id={'schufa-solvency-image-mobile'}
            className={`${styles.previewImage} lap-hide desk-hide`}
            src={schufaWithButtonMobile}
            alt={''}
          />
        </Button>
        <div
          style={{ width: '63%' }}
          className={'palm-one-whole grid-align-stretch lap-padding-left-s desk-padding-left-s grid-flex'}
        >
          <ul className={'list-check'}>
            {de.benefits.schufaCheckMieterPlus.schufaModal.description.map((descriptionPart, index) => (
              <li key={index} className={'padding-bottom-s'}>
                {descriptionPart}
              </li>
            ))}
          </ul>
          <Button
            id={'create-schufa-button'}
            appearance={'secondary'}
            className={`one-whole grid-align-end desk-margin-bottom-xs lap-margin-bottom-xs palm-margin-top-xl ${styles.createSolvencyCta}`}
            size={'small'}
            onClick={() => window.location.assign(schufaCheckUrl)}
          >
            {t({
              id: 'benefits.schufaCheckMieterPlus.schufaModal.create',
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SchufaChoice: React.FC<SchufaChoiceProps> = ({ solvencyInfo }) => {
  const { t } = useIntl();
  const [showPreview, setShowPreview] = useState<boolean>(false);

  if (showPreview) {
    return <SchufaPreview onClose={() => setShowPreview(false)} />;
  }

  return (
    <div className={styles.container} data-testid='schufa-choice'>
      <Heading4>
        {t({
          id: 'benefits.schufaCheckMieterPlus.schufaModal.headline',
        })}
      </Heading4>
      <AlreadyBoughtBanner />

      <div className={'grid grid-flex gutter-m padding-bottom-m grid-align-center'}>
        <div className={'grid-item one-whole'}>
          <SchufaSolvencyCard solvencyInfo={solvencyInfo} setShowPreview={setShowPreview} />
        </div>

        <div className={'grid-item one-whole'}>
          {t({
            id: `benefits.schufaCheckMieterPlus.schufaModal.is24SolvencyIncluded.prefix`,
            defaultMessage: 'Zusätzliches MieterPlus-Angebot: ',
          })}
          <Link href={is24SolvencyUrl} variant='default' rightIcon={<IconS24ChevronRight24 />}>
            {t({
              id: `benefits.schufaCheckMieterPlus.schufaModal.is24SolvencyIncluded.link`,
              defaultMessage: 'ImmoScout24 Bonitätsauskunft',
            })}
          </Link>
        </div>
      </div>
    </div>
  );
};
